import { useState } from 'react';
import { useLocation } from 'react-router';
import { getIDFromUrl } from '@rossum/api-client';
import {
  AUTO_DETECT,
  getTranslateLanguage,
  TranslateLanguage,
  TranslateSourceLanguage,
} from '../languages';
import { useTranslateDocument } from './useTranslateDocument';

type UseDetectedSourceLanguageProps = {
  targetLanguage: TranslateLanguage | undefined;
  sourceLanguage: TranslateSourceLanguage;
  pageNumber: number;
};

/**
 * Source language the translation service detected, read from the cache entry
 * owned by the rendered page — this observer never fires a request of its own.
 *
 * Detection happens per page, but the panel shows a single value for the whole
 * document, so the first language detected wins until the language pair (or the
 * document) changes. That also keeps the label stable while scrolling into
 * pages whose translation has not been fetched yet.
 */
export const useDetectedSourceLanguage = ({
  targetLanguage,
  sourceLanguage,
  pageNumber,
}: UseDetectedSourceLanguageProps): TranslateLanguage | undefined => {
  const location = useLocation();
  const annotationId = getIDFromUrl(location.pathname);

  const { data } = useTranslateDocument({
    annotationId,
    targetLanguage,
    sourceLanguage,
    pageNumber,
    observeOnly: true,
  });

  const detected = getTranslateLanguage(
    data?.results.find(result => result.pageNumber === pageNumber)
      ?.sourceLanguage
  );

  const key = `${annotationId}|${targetLanguage}|${sourceLanguage}`;

  const [sticky, setSticky] = useState<{
    key: string;
    language: TranslateLanguage | undefined;
  }>({ key, language: detected });

  const isStale = sticky.key !== key;
  const isFirstDetection = detected !== undefined && !sticky.language;

  if (isStale || isFirstDetection) {
    setSticky({ key, language: detected });
  }

  return sourceLanguage === AUTO_DETECT ? sticky.language : undefined;
};
